import { useState, type FC, type HTMLAttributeAnchorTarget } from 'react';
import { graphql, useFragment } from 'react-relay';

import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { ProductImage } from './ProductImage.tsx';
import ProductVideo from './ProductVideo.tsx';
import {
    type ImageBackgroundColor,
    type OnClick,
    type SrcSet,
    type TileSize,
} from '../constants.ts';

import { type ProductCarouselItem_viewer$key } from './__generated__/ProductCarouselItem_viewer.graphql.ts';
import { type ProductCarouselItem_item$key } from './__generated__/ProductCarouselItem_item.graphql.ts';
import { type ProductCarouselItem_photos$key } from './__generated__/ProductCarouselItem_photos.graphql.ts';

const viewerFragment = graphql`
    fragment ProductCarouselItem_viewer on Viewer {
        ...ProductVideo_viewer
    }
`;

const itemFragment = graphql`
    fragment ProductCarouselItem_item on Item
    @argumentDefinitions(fetchVideo: { type: "Boolean", defaultValue: false }) {
        hasApprovedVideo @include(if: $fetchVideo)
        ...ProductImage_item
        ...ProductVideo_item
    }
`;

const photoFragment = graphql`
    fragment ProductCarouselItem_photos on ItemPhoto {
        ...ProductImage_photo
    }
`;

type Props = {
    viewer: ProductCarouselItem_viewer$key | null | undefined;
    item: ProductCarouselItem_item$key | null | undefined;
    photo: ProductCarouselItem_photos$key | null | undefined;
    disableLinks?: boolean;
    imageBackgroundColor?: ImageBackgroundColor;
    imageLoadVerticalOffset?: number;
    isEagerImageLoadEnabled: boolean;
    isHovering: boolean;
    linkTarget?: HTMLAttributeAnchorTarget;
    onClick?: OnClick;
    onContentLoaded?: () => void;
    photoIndex: number;
    size: TileSize;
    srcSet?: SrcSet;
};

export const ProductCarouselItem: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    photo: photoRef,
    disableLinks,
    isEagerImageLoadEnabled,
    imageBackgroundColor,
    imageLoadVerticalOffset,
    isHovering,
    linkTarget,
    onClick,
    onContentLoaded,
    photoIndex,
    size,
    srcSet,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const photo = useFragment(photoFragment, photoRef);
    const [isVideoReady, setIsVideoReady] = useState(false);
    const { hasApprovedVideo } = item || {};
    const isClient = useClientState();

    const content = (
        <ProductImage
            disableLinks={disableLinks}
            hasVideo={photoIndex === 0 && hasApprovedVideo && isVideoReady}
            imageBackgroundColor={imageBackgroundColor}
            imageDraggable={false}
            imageLoadVerticalOffset={imageLoadVerticalOffset}
            item={item}
            isEagerImageLoadEnabled={isEagerImageLoadEnabled}
            linkTarget={linkTarget}
            onClick={onClick}
            onContentLoaded={onContentLoaded}
            photo={photo}
            size={size}
            srcSet={srcSet}
        />
    );

    return photoIndex === 0 && hasApprovedVideo && isClient ? (
        <ProductVideo
            viewer={viewer}
            item={item}
            imageBackgroundColor={imageBackgroundColor}
            isHovering={isHovering}
            onPlay={() => {
                setIsVideoReady(true);
            }}
        >
            {content}
        </ProductVideo>
    ) : (
        content
    );
};
